import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Music, Award, Clock, TrendingUp, BarChart3, MessageSquare } from 'lucide-react';
import type { ParticipantRecord } from './participant-browser';

interface ParticipantAnalyticsProps {
  participants: ParticipantRecord[];
  className?: string;
}

export function ParticipantAnalytics({ participants, className }: ParticipantAnalyticsProps) {
  // Calculate analytics
  const totalParticipants = participants.length;
  const avgAnnotations = Math.round(participants.reduce((sum, p) => sum + p.annotationCount, 0) / totalParticipants);
  const avgTrainingYears = Math.round(participants.reduce((sum, p) => sum + p.formalTrainingYears, 0) / totalParticipants);
  
  // Feedback analytics
  const participantsWithFeedback = participants.filter(p => p.feedbackMetrics && p.feedbackMetrics.feedbackCount > 0);
  const feedbackEngagementRate = totalParticipants > 0 ? (participantsWithFeedback.length / totalParticipants) * 100 : 0;
  const totalFeedbackSubmissions = participants.reduce((sum, p) => sum + (p.feedbackMetrics?.feedbackCount || 0), 0);
  const avgFeedbackLength = participantsWithFeedback.length > 0 
    ? Math.round(participantsWithFeedback.reduce((sum, p) => sum + (p.feedbackMetrics?.averageFeedbackLength || 0), 0) / participantsWithFeedback.length)
    : 0;
  
  // Engagement distribution
  const engagementDistribution = participants.reduce((acc, p) => {
    acc[p.musicalEngagement] = (acc[p.musicalEngagement] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Age distribution
  const ageDistribution = participants.reduce((acc, p) => {
    const ageGroup = p.ageRange || 'Unknown';
    acc[ageGroup] = (acc[ageGroup] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // AI experience distribution
  const aiExperienceDistribution = participants.reduce((acc, p) => {
    acc[p.aiMusicExperience] = (acc[p.aiMusicExperience] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // High contributors (>= 20 annotations)
  const highContributors = participants.filter(p => p.annotationCount >= 20).length;
  const contributionRate = Math.round((highContributors / totalParticipants) * 100);

  // Most popular genres
  const genreCount = participants.reduce((acc, p) => {
    p.musicalGenres.forEach(genre => {
      acc[genre] = (acc[genre] || 0) + 1;
    });
    return acc;
  }, {} as Record<string, number>);

  const topGenres = Object.entries(genreCount)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  // Feedback context distribution
  const feedbackContextDistribution = participants.reduce((acc, p) => {
    if (p.feedbackMetrics?.feedbackByContext) {
      Object.entries(p.feedbackMetrics.feedbackByContext).forEach(([context, count]) => {
        acc[context] = (acc[context] || 0) + count;
      });
    }
    return acc;
  }, {} as Record<string, number>);

  const getDistributionChart = (data: Record<string, number>, colors: string[]) => {
    const total = Object.values(data).reduce((sum, value) => sum + value, 0);
    const entries = Object.entries(data);

    return (
      <div className="space-y-2">
        {entries.map(([key, value], index) => {
          const percentage = total > 0 ? (value / total) * 100 : 0;
          const color = colors[index % colors.length];
          
          return (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm capitalize">{key.replace('-', ' ')}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{value}</div>
                <div className="text-xs text-muted-foreground">
                  {percentage.toFixed(1)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={className}>
      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <div className="text-2xl font-bold text-primary">{totalParticipants}</div>
              </div>
              <div className="text-sm text-muted-foreground">Total Participants</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-green-600" />
                <div className="text-2xl font-bold">{avgAnnotations}</div>
              </div>
              <div className="text-sm text-muted-foreground">Avg Annotations</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-purple-600" />
                <div className="text-2xl font-bold">{contributionRate}%</div>
              </div>
              <div className="text-sm text-muted-foreground">High Contributors</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Music className="h-4 w-4 text-orange-600" />
                <div className="text-2xl font-bold">{avgTrainingYears}</div>
              </div>
              <div className="text-sm text-muted-foreground">Avg Training Years</div>
            </CardContent>
          </Card>
        </div>

        {/* Feedback Engagement Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-blue-600" />
                <div className="text-2xl font-bold text-primary">{Math.round(feedbackEngagementRate)}%</div>
              </div>
              <div className="text-sm text-muted-foreground">Feedback Engagement Rate</div>
              <div className="text-xs text-muted-foreground mt-1">
                {participantsWithFeedback.length} of {totalParticipants} participants
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-green-600" />
                <div className="text-2xl font-bold">{totalFeedbackSubmissions}</div>
              </div>
              <div className="text-sm text-muted-foreground">Total Feedback Submissions</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-purple-600" />
                <div className="text-2xl font-bold">{avgFeedbackLength}</div>
              </div>
              <div className="text-sm text-muted-foreground">Avg Feedback Length (chars)</div>
            </CardContent>
          </Card>
        </div>

        {/* Distribution Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Musical Engagement Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Musical Engagement Levels
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getDistributionChart(engagementDistribution, ['#3b82f6', '#8b5cf6', '#06b6d4', '#f97316'])}
            </CardContent>
          </Card>

          {/* Age Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Age Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getDistributionChart(ageDistribution, ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'])}
            </CardContent>
          </Card>

          {/* AI Experience Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                AI Music Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getDistributionChart(aiExperienceDistribution, ['#ef4444', '#f97316', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'])}
            </CardContent>
          </Card>

          {/* Top Genres */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Music className="h-4 w-4" />
                Most Popular Genres
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {topGenres.map(([genre, count], index) => (
                  <div key={genre} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">#{index + 1}</div>
                      <Badge variant="outline">{genre}</Badge>
                    </div>
                    <div className="text-sm font-medium">{count} participants</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Feedback Context Distribution */}
          {Object.keys(feedbackContextDistribution).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Feedback by Context
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getDistributionChart(feedbackContextDistribution, ['#10b981', '#3b82f6', '#f59e0b'])}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Participation Quality Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              Participation Quality Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {participants.filter(p => p.annotationCount >= 30).length}
                </div>
                <div className="text-sm text-green-700 dark:text-green-300">Super Contributors (30+ annotations)</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {participants.filter(p => p.annotationCount >= 15 && p.annotationCount < 30).length}
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">Regular Contributors (15-29 annotations)</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {participants.filter(p => p.annotationCount < 15).length}
                </div>
                <div className="text-sm text-red-700 dark:text-red-300">New Contributors (&lt;15 annotations)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}